import { action, computed, observable, makeObservable } from 'mobx'
import { GenericList } from '.'
import type { IGenericListConfiguration } from '.'

export interface IGenericCudListConfiguration<ET> extends IGenericListConfiguration {
  idField?: string;
  nameField?: string;

  /** Returns default item to use for new entries. */
  defaultItem: () => ET;

  /** Validate given item. Return boolean true for valid item or string error. */
  validateItem?: (item: ET) => boolean | string;

  /** Transform given item into item suitable for serialization to server. */
  serverItem?: (item: ET) => unknown;

  endpointPost?: string;
  endpointPut?: string;
  endpointDelete?: string;
  endpointRestore?: string;
}

export class GenericCudList<T, ET> extends GenericList<T> {

  cudConfiguration: IGenericCudListConfiguration<ET>;

  constructor(cudConfiguration: IGenericCudListConfiguration<ET>) {
    super(cudConfiguration);

    makeObservable(this, {
      cudError: observable,
      setCudError: action,
      validationError: observable,
      setValidationError: action,
      hasCudOrValidationError: computed,
      createError: computed,
      updateError: computed,
      deleteError: computed,
      restoreError: computed,

      editableItem: observable,
      setEditableItem: action,
      editableItemUnmodified: observable,
      setEditableItemUnmodified: action,
      deleteItem: observable,
      setDeleteItem: action,
      deletedItem: observable,
      setDeletedItem: action,
      createdItem: observable,
      setCreatedItem: action,
      restoreItem: observable,
      setRestoreItem: action,
      restoredItem: observable,
      setRestoredItem: action,

      create: action,
      update: action,
      delete: action,
      restore: action,
      cancel: action,
      isCreate: computed,
      isUpdate: computed,
      isDelete: computed,
      isRestore: computed,
      isModified: computed,
      isValidated: computed,

      save: action,
      deleteConfirmed: action,
      restoreConfirmed: action,
    });

    this.cudConfiguration = cudConfiguration;
  }

  /** Set in case of Create Update Delete error. Prefer using specialized error fields per mode. */
  cudError?: Error | string;
  setCudError(value?: Error | string) {
    this.cudError = value;
  }

  /** Might be set in case custom validation method returned error message. */
  validationError?: string;
  setValidationError(value?: string) {
    this.validationError = value;
  }

  /** Set in case of CUD error or validation error (latter only when in update mode). */
  get hasCudOrValidationError() {
    return ((this.isCreate || this.isUpdate) && (this.cudError !== undefined)) ||
      (this.isUpdate && this.validationError !== undefined)
  }

  /** Set after error in create mode. */
  get createError() {
    return (this.isCreate && this.cudError !== undefined) ? this.cudError : undefined;
  }

  /** Set after error in update mode. */
  get updateError() {
    return (this.isUpdate && this.cudError !== undefined) ? this.cudError : undefined;
  }

  /** Set after error in delete mode. */
  get deleteError() {
    return (this.isDelete && this.cudError !== undefined) ? this.cudError : undefined;
  }

  /** Set after error in restore mode. */
  get restoreError() {
    return (this.isRestore && this.cudError !== undefined) ? this.cudError : undefined;
  }

  /** Set to editable item when in create or update mode. */
  editableItem?: ET;
  setEditableItem(value?: ET) {
    this.editableItem = value;

    const validationerror = value !== undefined && this.cudConfiguration.validateItem && this.cudConfiguration.validateItem(value);
    if (typeof validationerror === 'string') {
      this.setValidationError(validationerror);
    } else {
      this.setValidationError(undefined);
    }
  }

  /** Set to original editable item when starting update mode. */
  editableItemUnmodified?: ET;
  setEditableItemUnmodified(value?: ET) {
    this.editableItemUnmodified = value;
  }

  /** Set to item when in delete mode. */
  deleteItem?: T;
  setDeleteItem(value?: T) {
    this.deleteItem = value;
  }

  /** Set with data of just deleted item. */
  deletedItem?: T;
  setDeletedItem(value?: T) {
    this.deletedItem = value;
  }

  /** Set with data of newly created item. */
  createdItem?: T;
  setCreatedItem(value?: T) {
    this.createdItem = value;
  }

  /** Set to item when in restore mode. */
  restoreItem?: T;
  setRestoreItem(value?: T) {
    this.restoreItem = value;
  }

  /** Set with data of just restored item. */
  restoredItem?: T;
  setRestoredItem(value?: T) {
    this.restoredItem = value;
  }
  /** Start create mode for item. */
  create = (template?: ET) => {
    this.deleteItem = undefined;
    this.restoreItem = undefined;
    this.editableItem = template ?? this.cudConfiguration.defaultItem();
    this.editableItemUnmodified = undefined;
    this.cudError = undefined;
    this.validationError = undefined;
  }
  /** Start update mode for given item. */
  update(entity: ET | T) {
    this.deleteItem = undefined;
    this.restoreItem = undefined;
    this.editableItem = { ...entity as ET };
    this.editableItemUnmodified = { ...entity as ET };
    this.cudError = undefined;
    this.validationError = undefined;
  }

  /** Start delete mode for given item. */
  delete(entity: T) {
    this.editableItem = undefined;
    this.editableItemUnmodified = undefined;
    this.deleteItem = entity;
    this.restoreItem = undefined;
    this.cudError = undefined;
    this.validationError = undefined;
  }

  /** Start restore mode for given item. */
  restore(entity: T) {
    this.editableItem = undefined;
    this.editableItemUnmodified = undefined;
    this.deleteItem = undefined;
    this.restoreItem = entity;
    this.cudError = undefined;
    this.validationError = undefined;
  }

  /** Cancel any mode we are currently in (create, update, delete, restore). Using lambda syntax here so we can directly hook this method to onClick/onChange like events of user interface. */
  cancel = () => {
    this.editableItem = undefined;
    this.editableItemUnmodified = undefined;
    this.deleteItem = undefined;
    this.restoreItem = undefined;
    this.cudError = undefined;
    this.validationError = undefined;
  }

  /** True if in create mode. */
  get isCreate() {
    return this.editableItem !== undefined && this.idForItem(this.editableItem) === undefined;
  }

  /** True if in update mode. */
  get isUpdate() {
    return this.editableItem !== undefined && this.idForItem(this.editableItem) !== undefined;
  }

  /** True if in delete mode. */
  get isDelete() {
    return this.deleteItem !== undefined;
  }

  /** True if in restore mode. */
  get isRestore() {
    return this.restoreItem !== undefined;
  }

  /** True is editable data was changed while in update mode. */
  get isModified() {
    return this.editableItemUnmodified === undefined || JSON.stringify(this.editableItemUnmodified) !== JSON.stringify(this.editableItem);
  }

  /** True if editable data contains valid data while in create or update mode. */
  get isValidated() {
    return this.editableItem && (!this.cudConfiguration.validateItem || this.cudConfiguration.validateItem(this.editableItem) === true);
  }

  /** Perform actual create or update operation of item set in create or update mode. */
  async save() {
    this.setCudError(undefined);
    this.setCreatedItem(undefined);

    if (this.settings.token && this.editableItem) {
      try {
        // Construct item specifically for server to prevent overposting of data not used by server
        const serveritem = this.cudConfiguration.serverItem ?
          this.cudConfiguration.serverItem(this.editableItem) :
          { ...this.editableItem };

        if (this.isCreate) {
          const created = await this.settings.onFetch<T>(this.cudConfiguration.endpointPost ?? this.configuration.endpoint, {
            method: 'post',
            headers: this.settings.onGetHeaders(this.settings.token, this.settings.language, undefined, this.id),
            json: serveritem
          }, this.id);

          this.cancel();
          this.setCreatedItem(created);

          return true;
        } else if (this.isUpdate && this.idForItem(this.editableItem) !== undefined) {
          await this.settings.onFetch(`${this.cudConfiguration.endpointPut ?? this.configuration.endpoint}/${this.idForItem(this.editableItem)}`, {
            method: 'put',
            headers: this.settings.onGetHeaders(this.settings.token, this.settings.language, undefined, this.id),
            json: serveritem
          }, this.id);

          this.setEditableItemUnmodified({ ...this.editableItem });
          return true;
        }
      } catch (e) {
        if (e instanceof Error) {
          this.setCudError(e);
        }
      }
    }
    return false;
  }

  /** Perform actual delete operation of item previously set in delete mode. */
  async deleteConfirmed() {
    this.setCudError(undefined);

    if (this.isDelete && this.deleteItem && this.settings.token) {
      try {
        await this.settings.onFetch(`${this.cudConfiguration.endpointDelete ?? this.configuration.endpoint}/${this.idForItem(this.deleteItem)}`, {
          method: 'delete',
          headers: this.settings.onGetHeaders(this.settings.token, this.settings.language, undefined, this.id),
          // TODO: reintroduce retry
          //retry: 0
        }, this.id);

        this.setDeletedItem(this.deleteItem);
        this.cancel();
        return true;
      } catch (e) {
        if (e instanceof Error) {
          this.setCudError(e);
        }
      }
    }
    return false;
  }

  /** Perform actual restore operation of item previously set in restore mode. */
  async restoreConfirmed() {
    this.setCudError(undefined);

    if (this.isRestore && this.restoreItem && this.settings.token) {
      try {
        await this.settings.onFetch(`${this.cudConfiguration.endpointRestore ?? this.configuration.endpoint}/restore/${this.idForItem(this.restoreItem)}`, {
          method: 'post',
          headers: this.settings.onGetHeaders(this.settings.token, this.settings.language, undefined, this.id),
          // TODO: reintroduce retry
          //retry: 0
        }, this.id);

        this.setRestoredItem(this.restoreItem);
        this.cancel();
        return true;
      } catch (e) {
        if (e instanceof Error) {
          this.setCudError(e);
        }
      }
    }
    return false;
  }

  /** Return id of given item using settings provided in provided @see IGenericCudListConfiguration. */
  idForItem(item: T | ET): string | number | undefined {
    const idField = this.cudConfiguration.idField ?? 'id';
    return this.getNestedProperty(item, idField);
  }

  /** Return name of given item using settings provided in provided @see IGenericCudListConfiguration. */
  nameForItem(item: T | ET): string | number | undefined {
    const nameField = this.cudConfiguration.nameField ?? 'name';
    return this.getNestedProperty(item, nameField);
  }

  /** Helper function to get nested property values using dot notation (e.g., "user.name.surname"). */
  private getNestedProperty(obj: any, path: string): string | number | undefined {
    if (!obj || !path) {
      return undefined;
    }

    // Split the path by dots and traverse the object
    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return undefined;
      }
      current = current[key];
    }

    // Return the final value if it's a string or number, otherwise undefined
    return (typeof current === 'string' || typeof current === 'number') ? current : undefined;
  }
}